import produce from 'immer';
import { difference, intersection, isEqual, mapValues, omit } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router';
import {
  languageId,
  useApolloGraphqlDiagnostics,
} from 'register-apollo-graphql-language';

import {
  API_SCHEMA_ID,
  SUPERGRAPH_SCHEMA_ID,
  SchemaInfo,
} from 'src/app/graph/documentationPage/sdlPage/sdlDisplay/SDLDisplay';
import { SDL_EDITOR_ID } from 'src/app/graph/documentationPage/sdlPage/sdlDisplay/sdlEditorId';
import { uriToServiceName } from 'src/app/graph/documentationPage/sdlPage/sdlDisplay/uriToServiceName';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { proposalEditorRouteConfig } from 'src/app/graph/routes';
import { useShowToast } from 'src/components/toast/Toast';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { MONACO_READONLY_OPTIONS } from 'src/hooks/useStudioEditor';
import { useTabbedStudioEditor } from 'src/hooks/useTabbedStudioEditor';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import {
  doesConflictExist,
  generate3WayMerge,
} from '../../../diffHelpers/generateGitStyleMerge';
import { hasGraphqlJsSyntaxErrors } from '../../proposalSDLDisplay/proposalSDLEditor/getSDLErrors';
import { useLazySchemaFromHash } from '../useSchemaFromHash';

import { useFetchUpstreamSourceVariantSdlsBySubgraph } from './useFetchUpstreamSourceVariantSdlsBySubgraph';

const DEFAULT_EDITOR_TEXT = `extend schema @contact(
  description: "Send urgent issues to @{your team name}",
  name : "{Your Team}",
  url : "{your team\'s slack channel}"
)

directive @contact(
  "Contact title of the subgraph owner"
  name: String!,
  "Other relevant notes can be included here; supports markdown"
  description: String,
  "URL where the subgraph can be reached"
  url: String
) on SCHEMA
`;

function getProposalsCodeEditorOpener(
  openFileRef: React.MutableRefObject<
    (serviceName: string, range?: monacoEditor.IRange | undefined) => void
  >,
): monacoEditor.editor.ICodeEditorOpener {
  return {
    openCodeEditor: (__, resource, selection) => {
      openFileRef.current(
        uriToServiceName(resource),
        selection && 'startLineNumber' in selection ? selection : undefined,
      );
      return true;
    },
  };
}

export type ProposalSchemaInfo = Omit<
  SchemaInfo,
  | 'typeDefs'
  | 'typeCount'
  | 'fieldCount'
  | 'isLive'
  | 'version'
  | 'url'
  | 'lastUpdated'
> & {
  initialText?: string;
  firstLaunchHash?: string;
  hash: string | undefined;
  sdlIsLoading: boolean;
};

export const useProposalEditorState = ({
  graphRef,
  apiSchemaInfo,
  supergraphInfo,
  subgraphs,
  mergeBaseOrFirstLaunch,
  canEditProposal,
  shouldPullUpstreamChanges,
  mergeBaseCompositionId,
  sourceGraphVariant,
}: {
  graphRef: GraphRef;
  sourceGraphVariant: string | undefined;
  apiSchemaInfo: ProposalSchemaInfo | null;
  supergraphInfo: ProposalSchemaInfo | null;
  mergeBaseOrFirstLaunch:
    | NonNullable<
        NonNullable<
          NonNullable<
            NonNullable<GraphQLTypes.ProposalEditorQuery['service']>['variant']
          >['proposal']
        >['latestRevision']
      >['mergeBaseLaunch']
    | undefined;
  subgraphs:
    | Extract<
        NonNullable<
          NonNullable<
            NonNullable<GraphQLTypes.ProposalEditorQuery['service']>['variant']
          >['latestLaunch']
        >['buildInput'],
        { __typename: 'CompositionBuildInput' }
      >['subgraphs']
    | undefined;
  canEditProposal: boolean;
  shouldPullUpstreamChanges: boolean;
  mergeBaseCompositionId: string | null | undefined;
}) => {
  const { selectedSchema, newSubgraphName } = useRouteParams(
    proposalEditorRouteConfig,
  );

  const [changedSubgraphs, setChangedSubgraphs] = useState<
    Record<
      string,
      {
        changed: boolean;
        isEmpty: boolean;
        hasGraphqlJsSyntaxErrors: boolean;
        hasConflicts: boolean;
        // When the user pulls changes from the source, some initially unedited schemas may
        // appear in the edited column. These should remain marked as "unedited" unless the user
        // changes them.
        isUneditedFromLatestSourceVariantSdl: boolean;
      }
    >
  >({});
  const selectedSchemaValuesRef = useRef<Record<string, string>>({});

  const [subgraphsWithMetadata, setSubgraphsWithMetadata] = useState<
    [string, ProposalSchemaInfo][]
  >([]);

  const history = useHistory();
  const location = useLocation();
  const setSelectedSchemaName = useCallback(
    (schemaName: string | undefined) => {
      history.push(
        proposalEditorRouteConfig.locationFrom({
          location,
          patch: {
            selectedSchema: schemaName,
            newSubgraphName: RouteConfig.DELETE,
            linkedCoordinateName: RouteConfig.DELETE,
            linkedCommentId: RouteConfig.DELETE,
          },
        }),
      );
    },
    [history, location],
  );

  useEffect(() => {
    setSubgraphsWithMetadata((currentValues) => {
      if (
        currentValues.length === 0 ||
        subgraphs?.some(
          (subgraph) =>
            !currentValues.some(
              ([subgraphName]) => subgraphName === subgraph.name,
            ),
        )
      ) {
        const newSubgraphsWithMetadata =
          subgraphs
            ?.map((subgraph): [string, ProposalSchemaInfo] => {
              const firstLaunchHash =
                mergeBaseOrFirstLaunch?.buildInput.__typename ===
                'CompositionBuildInput'
                  ? mergeBaseOrFirstLaunch.buildInput.subgraphs.find(
                      (s) => s.name === subgraph.name,
                    )?.hash
                  : undefined;
              return [
                subgraph.name,
                {
                  type: 'Subgraph',
                  id: subgraph.name,
                  displayName: subgraph.name,
                  // we fetch the schema on demand when a user selects a subgraph,
                  // to avoid slow queries for subgraphs that will never be seen.
                  // This text starts out as empty until selected
                  sdlIsLoading: subgraph.sdl === undefined,
                  text: subgraph.sdl ?? '',
                  initialText: subgraph.sdl,
                  hash: subgraph.hash,
                  firstLaunchHash,
                  language: languageId,
                  subgraphName: subgraph.name,
                },
              ];
            })
            .concat(
              currentValues.filter(
                ([subgraphName]) =>
                  !subgraphs.some((subgraph) => subgraphName === subgraph.name),
              ),
            )
            .sort(([a], [b]) => a.localeCompare(b)) ?? [];
        return newSubgraphsWithMetadata;
      }
      const nextSubgraphsWithMetadata = currentValues.map<
        [string, ProposalSchemaInfo]
      >(([name, info]) => {
        const newText = selectedSchemaValuesRef?.current?.[name];

        return [
          name,
          {
            ...info,
            ...(newText && { text: newText }),
          },
        ];
      });
      if (isEqual(nextSubgraphsWithMetadata, currentValues)) {
        return currentValues;
      }
      return nextSubgraphsWithMetadata;
    });
  }, [mergeBaseOrFirstLaunch, subgraphs]);

  const subgraphsFromBuildInputBySubgraph = useMemo(() => {
    return subgraphs?.reduce(
      (acc, subgraph) => ({
        ...acc,
        [subgraph.name]: subgraph,
      }),
      {} as Record<string, NonNullable<typeof subgraphs>[number]>,
    );
  }, [subgraphs]);

  const {
    baseSourceVariantBuildInputBySubgraph,
    latestSourceVariantBuildInputBySubgraph,
    loading,
  } = useFetchUpstreamSourceVariantSdlsBySubgraph({
    graphRef,
    shouldPullUpstreamChanges,
    mergeBaseCompositionId,
    sourceGraphVariant,
  });

  const shouldShowMergeConflicts =
    shouldPullUpstreamChanges &&
    latestSourceVariantBuildInputBySubgraph &&
    !loading;

  useEffect(() => {
    if (shouldShowMergeConflicts) {
      setSubgraphsWithMetadata((currentSubgraphs) => {
        // find subgraphs that were deleted in the proposal but still in the latest
        const proposalsSubgraphs = currentSubgraphs.map(
          ([subgraphName]) => subgraphName,
        );
        const baseSourceVariantSubgraphs = Object.keys(
          baseSourceVariantBuildInputBySubgraph || {},
        );
        const latestSourceVariantSubgraphs = Object.keys(
          latestSourceVariantBuildInputBySubgraph || {},
        );
        const deletedSubgraphs = difference(
          intersection(
            baseSourceVariantSubgraphs,
            latestSourceVariantSubgraphs,
          ),
          proposalsSubgraphs,
        );
        const deletedSubgraphsWithMeta = deletedSubgraphs.flatMap<
          [string, ProposalSchemaInfo]
        >((subgraphName) => {
          const sourceVariantBaseSdl =
            baseSourceVariantBuildInputBySubgraph?.[subgraphName]?.sdl;
          const sourceVariantLatestSdl =
            latestSourceVariantBuildInputBySubgraph?.[subgraphName]?.sdl;

          if (sourceVariantBaseSdl && sourceVariantLatestSdl) {
            const mergedSdls = generate3WayMerge(
              sourceVariantBaseSdl,
              sourceVariantLatestSdl,
              '',
            );
            selectedSchemaValuesRef.current[subgraphName] = mergedSdls;
            return [
              [
                subgraphName,
                {
                  type: 'Subgraph',
                  id: subgraphName,
                  displayName: subgraphName,
                  sdlIsLoading: false,
                  text: mergedSdls,
                  hash: 'ui-generated',
                  language: languageId,
                  subgraphName,
                },
              ],
            ];
          }
          return [];
        });
        // find subgraphs that were added in source variant
        const addedSubgraphs = difference(
          latestSourceVariantSubgraphs,
          baseSourceVariantSubgraphs,
          proposalsSubgraphs,
        );

        const addedSubgraphsWithMeta = addedSubgraphs.flatMap<
          [string, ProposalSchemaInfo]
        >((subgraphName) => {
          const sourceVariantLatestSdl =
            latestSourceVariantBuildInputBySubgraph?.[subgraphName]?.sdl;

          if (sourceVariantLatestSdl) {
            selectedSchemaValuesRef.current[subgraphName] =
              sourceVariantLatestSdl;
            return [
              [
                subgraphName,
                {
                  type: 'Subgraph',
                  id: subgraphName,
                  displayName: subgraphName,
                  sdlIsLoading: false,
                  text: sourceVariantLatestSdl,
                  hash: 'ui-generated',
                  language: languageId,
                  subgraphName,
                },
              ],
            ];
          }
          return [];
        });

        const nextSubgraphsWithMetadataWithDeletedAndAdded = [
          ...deletedSubgraphsWithMeta,
          ...addedSubgraphsWithMeta,
          ...currentSubgraphs,
        ];
        setChangedSubgraphs((currentValues) => ({
          ...currentValues,
          ...[...deletedSubgraphsWithMeta, ...addedSubgraphsWithMeta].reduce(
            (acc, [subgraphName, { text }]) => ({
              ...acc,
              [subgraphName]: {
                changed: true,
                hasGraphqlJsSyntaxErrors: hasGraphqlJsSyntaxErrors(text),
                hasConflicts: doesConflictExist(text),
              },
            }),
            {},
          ),
        }));
        if (
          isEqual(
            nextSubgraphsWithMetadataWithDeletedAndAdded,
            currentSubgraphs,
          )
        ) {
          return currentSubgraphs;
        }
        return nextSubgraphsWithMetadataWithDeletedAndAdded;
      });
    }
  }, [
    shouldShowMergeConflicts,
    baseSourceVariantBuildInputBySubgraph,
    latestSourceVariantBuildInputBySubgraph,
  ]);

  // Show a toast when changes are pulled into the editor
  const [currentToastId, setCurrentToastId] = useState<string>();
  const { showToasts, hideToasts } = useShowToast();
  useEffect(() => {
    if (
      subgraphsWithMetadata.length > 0 &&
      shouldPullUpstreamChanges &&
      !currentToastId
    ) {
      const toastIds = showToasts({
        theme: 'dark',
        level: 'info',
        heading: 'Changes pulled into editor',
        message:
          'This is the merge result. Please resolve any conflicts, and save merge to complete pulling changes from source.',
      });
      setCurrentToastId(toastIds?.[0]);
    }
  }, [
    currentToastId,
    shouldPullUpstreamChanges,
    showToasts,
    subgraphsWithMetadata.length,
  ]);

  // NOTE: Hide toast on location change
  React.useEffect(() => {
    if (currentToastId) return () => hideToasts(currentToastId);
  }, [currentToastId, hideToasts, location.pathname]);

  // Hide toast after 30 seconds always
  React.useEffect(() => {
    if (currentToastId) {
      const timeout = window.setTimeout(
        () => hideToasts(currentToastId),
        30 * 1000,
      );
      return () => window.clearTimeout(timeout);
    }
  }, [hideToasts, currentToastId]);

  const hasSubgraphBeenSaved = useCallback(
    (subgraphName: string) =>
      subgraphs?.some((subgraph) => subgraph.name === subgraphName),
    [subgraphs],
  );

  const { getLazySchemaFromHash } = useLazySchemaFromHash();

  const fetchSelectedSchema = useCallback(
    async (schemaHash: string) => {
      const lazySchemaFromHash = await getLazySchemaFromHash({
        variables: {
          graphId: graphRef.graphId,
          schemaHash,
        },
      });
      const schema = lazySchemaFromHash.data?.service?.doc?.source;
      return schema;
    },
    [getLazySchemaFromHash, graphRef.graphId],
  );

  const updateTextForSubgraph = useCallback(
    async ({
      subgraphIdToUpdate,
      newSubgraph,
      shouldUpdateText,
      shouldUpdateInitialText,
    }: {
      subgraphIdToUpdate: string;
      newSubgraph:
        | { type: 'hash'; newHash: string }
        | { type: 'sdl'; newSdl: string };
      shouldUpdateText: boolean;
      shouldUpdateInitialText: boolean;
    }) => {
      const schemaSdl =
        newSubgraph.type === 'hash'
          ? await fetchSelectedSchema(newSubgraph.newHash)
          : newSubgraph.newSdl;

      if (schemaSdl) {
        setSubgraphsWithMetadata((currentSubgraphs) =>
          produce(currentSubgraphs, (draftSubgraphs) => {
            const draftSubgraphIndex = draftSubgraphs.findIndex(
              ([subgraph]) => subgraph === subgraphIdToUpdate,
            );
            const currentSubgraphInfo =
              draftSubgraphIndex !== -1
                ? draftSubgraphs[draftSubgraphIndex]?.[1]
                : undefined;

            if (currentSubgraphInfo && draftSubgraphIndex !== -1) {
              // eslint-disable-next-line no-param-reassign
              draftSubgraphs[draftSubgraphIndex] = [
                subgraphIdToUpdate,
                {
                  ...currentSubgraphInfo,
                  ...(newSubgraph.type === 'hash'
                    ? { hash: newSubgraph.newHash }
                    : {}),
                  ...(shouldUpdateText
                    ? { text: schemaSdl, sdlIsLoading: false }
                    : {}),
                  ...(shouldUpdateInitialText
                    ? { initialText: schemaSdl }
                    : {}),
                },
              ];
            }
          }),
        );
      }
    },
    [fetchSelectedSchema],
  );

  /**
   *  Set the initial text to the recently published text from the backend,
   *  and set the current text to the current state of the editor.
   *
   * Why do we need to set the current text to the state of the editor?
   * The `text` field on `subgraphsWithMetadata` is not kept up to date with the
   * state of the editor on keypress b/c the editor flickers, but when the editor rerenders
   * on subgraph switch or publish, we need to update the state b/c the editor
   * is a controlled component by `subgraphsWithMetadata`
   */
  const updateChangedSubgraphsTextPostPublish = useCallback(
    async (newSubgraphHashes: Record<string, string>) => {
      await Promise.all(
        Object.entries(changedSubgraphs).map(async ([changedSubgraphName]) => {
          const subgraphToUpdate = subgraphsWithMetadata.find(
            ([subgraphName]) => changedSubgraphName === subgraphName,
          );
          const subgraphId = subgraphToUpdate?.[0];
          const newHash = subgraphId
            ? newSubgraphHashes[subgraphId]
            : undefined;
          if (subgraphId && newHash) {
            await updateTextForSubgraph({
              subgraphIdToUpdate: subgraphId,
              // keep any changes the user made since saving
              shouldUpdateText: false,
              shouldUpdateInitialText: true,
              newSubgraph: {
                type: 'hash',
                newHash,
              },
            });

            const changedTextSdl = selectedSchemaValuesRef.current[subgraphId];
            if (changedTextSdl) {
              await updateTextForSubgraph({
                subgraphIdToUpdate: subgraphId,
                // keep any changes the user made since saving
                shouldUpdateText: true,
                shouldUpdateInitialText: false,
                newSubgraph: {
                  type: 'sdl',
                  newSdl: changedTextSdl,
                },
              });
            }
          }
        }),
      );
    },
    [changedSubgraphs, subgraphsWithMetadata, updateTextForSubgraph],
  );

  const storeSchemaChanges = useCallback(
    (schemaInfo: ProposalSchemaInfo | undefined) => () => {
      // If we are currently on a read-only editor we don't have anything to update
      if (
        schemaInfo?.id === API_SCHEMA_ID ||
        schemaInfo?.id === SUPERGRAPH_SCHEMA_ID
      ) {
        return;
      }
      setSubgraphsWithMetadata((currentSubgraphs) =>
        produce(currentSubgraphs, (draftSubgraphs) => {
          const draftSubgraphIndex = draftSubgraphs.findIndex(
            ([subgraph]) => subgraph === schemaInfo?.id,
          );

          if (
            draftSubgraphIndex !== -1 &&
            schemaInfo?.id &&
            draftSubgraphs[draftSubgraphIndex]
          ) {
            // eslint-disable-next-line no-param-reassign
            draftSubgraphs[draftSubgraphIndex] = [
              schemaInfo?.id,
              {
                ...schemaInfo,
                text: selectedSchemaValuesRef?.current?.[schemaInfo.id]
                  ? (selectedSchemaValuesRef.current[schemaInfo.id] ??
                    DEFAULT_EDITOR_TEXT)
                  : (schemaInfo.initialText ??
                    schemaInfo.text ??
                    DEFAULT_EDITOR_TEXT),
              },
            ];
          }
        }),
      );
    },
    [],
  );

  const graphInfoMap = useMemo((): Record<string, ProposalSchemaInfo> => {
    return {
      ...(apiSchemaInfo ? { [API_SCHEMA_ID]: apiSchemaInfo } : {}),
      ...(supergraphInfo ? { [SUPERGRAPH_SCHEMA_ID]: supergraphInfo } : {}),
      ...Object.fromEntries(subgraphsWithMetadata),
    };
  }, [apiSchemaInfo, subgraphsWithMetadata, supergraphInfo]);

  const selectedSchemaInfo = useMemo(() => {
    return selectedSchema ? graphInfoMap[selectedSchema] : undefined;
  }, [graphInfoMap, selectedSchema]);

  const loadedFiles = useMemo(() => {
    return Object.fromEntries(
      Object.entries(graphInfoMap).filter(([, file]) => !file.sdlIsLoading),
    );
  }, [graphInfoMap]);

  const { editor, modelsByName } = useTabbedStudioEditor({
    // NOTE: We should NOT turn this flag on for the proposal editor. It causes
    // unintended side effects due to us loading our composed schemas in the background
    // and causes the cursor to jump around when those are loaded. If you need to make
    // edits to the editor contents, set the models or execute edits directly instead.
    controlContents: false,
    editorId: SDL_EDITOR_ID,
    files: loadedFiles,
    selectedFile: selectedSchema,
    setSelectedFile: setSelectedSchemaName,
    getCodeEditorOpener: getProposalsCodeEditorOpener,
    ...(selectedSchemaInfo?.type === 'Graph' || !canEditProposal
      ? MONACO_READONLY_OPTIONS
      : {}),
    lineNumbers: 'on',
    glyphMargin: true,
    minimap: { enabled: true },
    lineGutterPaddingSpace: 3,
    maintainScrollPosition: true,
  });

  useEffect(() => {
    const disposables = Object.entries(modelsByName).flatMap(
      ([modelId, model]) => {
        if (modelId === API_SCHEMA_ID || modelId === SUPERGRAPH_SCHEMA_ID)
          return [];

        return model.onDidChangeContent(() => {
          const newSchemaText = model.getValue();
          selectedSchemaValuesRef.current = {
            ...selectedSchemaValuesRef.current,
            [modelId]: newSchemaText,
          };
          const changedSubgraph = subgraphsWithMetadata.find(
            ([subgraph]) => subgraph === modelId,
          )?.[1];
          const sourceVariantLatestSdl =
            latestSourceVariantBuildInputBySubgraph?.[modelId]?.sdl;

          setChangedSubgraphs((currentValues) => {
            const hasChanges = newSchemaText !== changedSubgraph?.initialText;
            const isEmpty = newSchemaText.trim() === '';
            const hasConflicts = doesConflictExist(newSchemaText);
            const isUneditedFromLatestSourceVariantSdl =
              !!shouldShowMergeConflicts &&
              sourceVariantLatestSdl === newSchemaText;
            const nextValue = {
              changed: hasChanges,
              isEmpty,
              hasGraphqlJsSyntaxErrors:
                hasChanges && hasGraphqlJsSyntaxErrors(newSchemaText),
              hasConflicts,
              isUneditedFromLatestSourceVariantSdl,
            };
            if (!isEqual(nextValue, currentValues[modelId])) {
              return { ...currentValues, [modelId]: nextValue };
            }
            return currentValues;
          });

          if (changedSubgraph?.id !== selectedSchemaInfo?.id) {
            storeSchemaChanges(changedSubgraph)();
          }
        });
      },
    );

    return () => disposables.forEach((disposable) => disposable.dispose());
  }, [
    latestSourceVariantBuildInputBySubgraph,
    modelsByName,
    selectedSchemaInfo?.id,
    shouldShowMergeConflicts,
    storeSchemaChanges,
    subgraphsWithMetadata,
  ]);

  const [
    hasCalculatedMergeConflictsBySubgraph,
    setHasCalculatedMergeConflictsBySubgraph,
  ] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (shouldShowMergeConflicts) {
      // loop through models and override with merged text
      Object.entries(modelsByName).forEach(([subgraphName, model]) => {
        const subgraph = subgraphsWithMetadata.find(
          ([s]) => s === subgraphName,
        )?.[1];

        if (!subgraph || hasCalculatedMergeConflictsBySubgraph[subgraphName]) {
          return;
        }

        const sourceVariantBaseSdl =
          baseSourceVariantBuildInputBySubgraph?.[subgraphName]?.sdl;
        const sourceVariantLatestSdl =
          latestSourceVariantBuildInputBySubgraph?.[subgraphName]?.sdl;
        const proposalSdl =
          selectedSchemaValuesRef.current[subgraphName] ??
          subgraphsFromBuildInputBySubgraph?.[subgraphName]?.sdl ??
          '';

        const mergedSdl =
          sourceVariantBaseSdl && sourceVariantLatestSdl
            ? generate3WayMerge(
                sourceVariantBaseSdl,
                sourceVariantLatestSdl,
                proposalSdl,
              )
            : null;
        if (mergedSdl && mergedSdl !== subgraph.text) {
          model.setValue(mergedSdl);
          setChangedSubgraphs((currentValues) => ({
            ...currentValues,
            ...(mergedSdl && {
              [subgraphName]: {
                changed: true,
                isEmpty: mergedSdl.trim() === '',
                hasGraphqlJsSyntaxErrors: hasGraphqlJsSyntaxErrors(mergedSdl),
                hasConflicts: doesConflictExist(mergedSdl),
                isUneditedFromLatestSourceVariantSdl: false,
              },
            }),
          }));
        }
        // We should mark subgraphs regardless of whether they have conflicts
        setHasCalculatedMergeConflictsBySubgraph((currentValues) => ({
          ...currentValues,
          [subgraphName]: true,
        }));
      });
    }
  }, [
    baseSourceVariantBuildInputBySubgraph,
    hasCalculatedMergeConflictsBySubgraph,
    latestSourceVariantBuildInputBySubgraph,
    modelsByName,
    setChangedSubgraphs,
    shouldShowMergeConflicts,
    subgraphsFromBuildInputBySubgraph,
    subgraphsWithMetadata,
  ]);

  const diagnostics = useApolloGraphqlDiagnostics();

  // Diagnostics from the language server
  const languageServerDiagnosticsBySubgraphName = useMemo(
    () =>
      mapValues(modelsByName, (value) => {
        const diagnosticsForSubgraph =
          diagnostics[value.uri.toString(true)] ?? [];
        return {
          syntax: diagnosticsForSubgraph.filter((d) => d.code === 'SYNTAX'),
          numberOfErrors: diagnosticsForSubgraph.filter(
            (d) => d.severity === monacoEditor.MarkerSeverity.Error,
          ).length,
        };
      }),
    [diagnostics, modelsByName],
  );

  useEffect(() => {
    setChangedSubgraphs((currentValues) => {
      const nextChangedSubgraphs = mapValues(
        currentValues,
        (_, subgraphName) => {
          const subgraph = subgraphsWithMetadata.find(
            ([s]) => s === subgraphName,
          );
          const currentEditorValue =
            selectedSchemaValuesRef?.current?.[subgraphName];

          const sourceVariantLatestSdl =
            latestSourceVariantBuildInputBySubgraph?.[subgraphName]?.sdl;

          const changed =
            !!subgraph &&
            (!hasSubgraphBeenSaved(subgraphName) ||
              (typeof currentEditorValue === 'string' &&
                subgraph[1].initialText !== undefined &&
                subgraph[1].initialText !== currentEditorValue));
          if (changed) {
            const isEmpty = currentEditorValue?.trim() === '';
            return {
              hasGraphqlJsSyntaxErrors: hasGraphqlJsSyntaxErrors(
                currentEditorValue ?? '',
              ),
              isEmpty,
              changed: true,
              hasConflicts: doesConflictExist(
                currentEditorValue ?? subgraph[1].text ?? '',
              ),
              isUneditedFromLatestSourceVariantSdl:
                !!shouldShowMergeConflicts &&
                sourceVariantLatestSdl === currentEditorValue,
            };
          } else {
            return {
              changed: false,
              isEmpty: false,
              hasGraphqlJsSyntaxErrors: false,
              hasConflicts: false,
              isUneditedFromLatestSourceVariantSdl: false,
            };
          }
        },
      );

      if (isEqual(nextChangedSubgraphs, currentValues)) {
        return currentValues;
      } else {
        return nextChangedSubgraphs;
      }
    });
  }, [
    hasSubgraphBeenSaved,
    latestSourceVariantBuildInputBySubgraph,
    shouldShowMergeConflicts,
    subgraphsWithMetadata,
  ]);

  const discardUnsavedSchemaChanges = useCallback(async () => {
    Object.entries(modelsByName).forEach(([subgraphName, model]) => {
      const subgraph = subgraphsWithMetadata.find(
        ([s]) => s === subgraphName,
      )?.[1];
      if (subgraph && selectedSchemaValuesRef.current[subgraphName]) {
        if (subgraph.initialText) {
          model.setValue(subgraph.initialText);
        } else {
          // If we have added any new subgraphs, we should remove them
          setSubgraphsWithMetadata((currentSubgraphs) =>
            currentSubgraphs.filter(([name]) => name !== subgraphName),
          );
        }
        delete selectedSchemaValuesRef.current[subgraphName];
      }
    });
    setHasCalculatedMergeConflictsBySubgraph({});
  }, [modelsByName, subgraphsWithMetadata]);

  const selectedSubgraphWithMetadata = useMemo(
    () =>
      subgraphsWithMetadata.find(
        (subgraph) => subgraph[0] === selectedSchemaInfo?.id,
      )?.[1],
    [selectedSchemaInfo, subgraphsWithMetadata],
  );

  const fetchInitialTextForSubgraph = useCallback(async () => {
    // If we are currently on a read-only editor we don't have anything to update
    if (
      selectedSchemaInfo?.id === API_SCHEMA_ID ||
      selectedSchemaInfo?.id === SUPERGRAPH_SCHEMA_ID
    ) {
      return;
    }

    const schemaHash = selectedSchemaInfo?.hash;
    if (
      schemaHash &&
      selectedSubgraphWithMetadata &&
      selectedSubgraphWithMetadata.initialText === undefined
    ) {
      updateTextForSubgraph({
        subgraphIdToUpdate: selectedSchemaInfo.id,
        // if we get here, this subgraph has no changes bc it hasn't been opened yet,
        // so update the text to be the initial text
        shouldUpdateText: true,
        shouldUpdateInitialText: true,
        newSubgraph: {
          type: 'hash',
          newHash: schemaHash,
        },
      });
    }
  }, [selectedSchemaInfo, selectedSubgraphWithMetadata, updateTextForSubgraph]);

  // fetch the initial text for subgraph every time the selectedSchema changes
  useEffect(() => {
    fetchInitialTextForSubgraph();
  }, [selectedSchema, fetchInitialTextForSubgraph]);

  const onAddSubgraph = useCallback(
    (name: string) => {
      if (
        subgraphsWithMetadata.some(([subgraphName]) => subgraphName === name)
      ) {
        return;
      }

      setSubgraphsWithMetadata((currentSubgraphs) =>
        produce(currentSubgraphs, (draftSubgraphs) => {
          draftSubgraphs.push([
            name,
            {
              type: 'Subgraph' as const,
              displayName: name,
              id: name,
              text: DEFAULT_EDITOR_TEXT,
              sdlIsLoading: false,
              hash: undefined,
              language: languageId,
              subgraphName: name,
            },
          ]);

          draftSubgraphs.sort(([a], [b]) => a.localeCompare(b));
        }),
      );

      setChangedSubgraphs((currentValues) => ({
        ...currentValues,
        [name]: {
          changed: true,
          isEmpty: false, // The default text is not empty
          hasGraphqlJsSyntaxErrors:
            hasGraphqlJsSyntaxErrors(DEFAULT_EDITOR_TEXT),
          hasConflicts: false,
          isUneditedFromLatestSourceVariantSdl: false,
        },
      }));
      selectedSchemaValuesRef.current[name] = DEFAULT_EDITOR_TEXT;
      setSelectedSchemaName(name);
    },
    [setSelectedSchemaName, subgraphsWithMetadata],
  );

  useEffect(() => {
    if (newSubgraphName) {
      onAddSubgraph(newSubgraphName);
    }
  }, [newSubgraphName, onAddSubgraph]);

  const onRenameSubgraph = useCallback(
    (formerSubgraphName: string, updatedSubgraphName: string) => {
      const subgraphValue =
        selectedSchemaValuesRef.current?.[formerSubgraphName] ??
        DEFAULT_EDITOR_TEXT;

      setChangedSubgraphs((currentValues) => {
        return {
          ...omit(currentValues, [formerSubgraphName]),
          [updatedSubgraphName]: {
            changed: true,
            isEmpty: subgraphValue.trim() === '',
            hasGraphqlJsSyntaxErrors: hasGraphqlJsSyntaxErrors(subgraphValue),
            hasConflicts: doesConflictExist(subgraphValue),
            isUneditedFromLatestSourceVariantSdl: false, // Only subgraphs that exist locally can be renamed.
          },
        };
      });

      selectedSchemaValuesRef.current = {
        ...selectedSchemaValuesRef.current,
        [updatedSubgraphName]: subgraphValue,
      };

      delete selectedSchemaValuesRef.current?.[formerSubgraphName];
    },
    [],
  );

  return {
    editor,
    modelsByName,
    languageServerDiagnosticsBySubgraphName,
    graphInfoMap,
    selectedSchemaInfo,
    subgraphsWithMetadata,
    setSubgraphsWithMetadata,
    changedSubgraphs,
    hasSubgraphBeenSaved,
    updateChangedSubgraphsTextPostPublish,
    storeSchemaChanges,
    discardUnsavedSchemaChanges,
    onAddSubgraph,
    onRenameSubgraph,
    setSelectedSchemaName,
  };
};
